import React from "react";

// types
import {
    CartItem,
    IDeliveryPromise,
    DeliveryPromiseType,
    GroupCartItemRendererProps,
    CartItemRendererProps,
    CartMenuItemsHeader,
    ReviewedCartApiResponse,
} from "@swiggy-private/cart-types";
import { ApiMenuCartItem } from "@swiggy-private/cart-types";
import { ReviewedCart } from "@cart-client/interfaces/Cart";
import { isItemAvailable } from "@swiggy-private/cart-ui";

// helpers
import { getCartMenuItemPropertyForUpdate } from "@cart-client/helpers/Cart";
import { isEmpty } from "@swiggy-private/js-utils";

// constants
import { CART_ITEM_REWARD_TYPE, DEAL_RUSH_GROUP_TAG_ID, MAGIC_ITEMS_GROUP_TAG_ID } from "../constants";

// components
import { Image } from "@swiggy-private/dls-web-core/dist/components/Image";

// types
import type { IColors } from "@swiggy-private/dls-web-common/dist/components/types.internal";

export interface CartItemWithUUID extends CartItem {
    unId: string;
    itemPosition: number;
}

export interface SelectiveDiscountingSection {
    header: {
        title: string;
        subtitle: string;
        subtitleColor: string;
        icon?: React.ReactNode;
        lottieId?: string;
        backgroundColor?: string;
        showAnimation?: boolean;
    };
    titleColor?: keyof IColors;
    items: CartItemWithUUID[];
}

export enum CartGroupList {
    COUPON_GROUP_ITEMS = "couponGroupItems",
    SILD_ITEMS = "sildItems",
    FREEBIE_ITEMS = "freebieItems",
    STEAL_DEAL_ITEMS = "stealDealItems",
    REGULAR_ITEMS = "regularItems",
    DEAL_RUSH_ITEMS = "dealRushItems",
    MAGIC_ITEMS = "magicItems",
}

/**
 * Return wether item is in stock
 * @param item
 */
export const isCartItemInStock = (item: CartItem | CartItemWithUUID): boolean => isItemAvailable(item);

export const isGroupTagtem = (item: CartItem | CartItemWithUUID): boolean => item.item_group_tag_id != null;

export const isFreebieItem = (item: CartItem | CartItemWithUUID): boolean => item.rewardType === "Freebie";

export const isStealDealItem = (item: CartItem | CartItemWithUUID): boolean =>
    item.rewardType === CART_ITEM_REWARD_TYPE.SLASH_DEAL;

export const isRegularItem = (item: CartItem | CartItemWithUUID): boolean => !item.item_group_tag_id;

export const isDealRushItem = (item: CartItem | CartItemWithUUID): boolean =>
    item.item_group_tag_id === DEAL_RUSH_GROUP_TAG_ID;

export const getUniqueIdForCartItem = (itemId: CartItem["menu_item_id"] | string, itemPosition: number): string => {
    const timestamp = new Date().getTime();
    return `${itemId} ${timestamp}  ${itemPosition}`;
};

export const isMagicItem = (item: CartItem | CartItemWithUUID): boolean =>
    item.item_group_tag_id === MAGIC_ITEMS_GROUP_TAG_ID;

/**
 *
 * @param cartItems
 * Returns items list wiht UUID for rendering and updating purpose
 */
export const createCartItemListWithUUID = (cartItems: CartItem[]): CartItemWithUUID[] => {
    const list: CartItemWithUUID[] = [];

    // if no items return empty list
    if (!cartItems || cartItems.length < 1) {
        return list;
    }

    // iterate over items and push to result list
    cartItems.map((item, index) => {
        list.push({ ...item, unId: getUniqueIdForCartItem(item.menu_item_id, index), itemPosition: index });
    });

    return list;
};

/**
 * Return all different group items list
 * @param itemList
 * @param groupTagDetails // group tag details from api response
 */
export const getCartGroupItems = (
    itemList: CartItemWithUUID[],
    groupTagDetails?: ReviewedCartApiResponse["group_tag_details"],
): { -readonly [k in keyof typeof CartGroupList]: CartItemWithUUID[] } => {
    // Create empty group list
    const groupItemsList: { -readonly [k in keyof typeof CartGroupList]: CartItemWithUUID[] } = {
        COUPON_GROUP_ITEMS: [],
        SILD_ITEMS: [],
        FREEBIE_ITEMS: [],
        STEAL_DEAL_ITEMS: [],
        REGULAR_ITEMS: [],
        DEAL_RUSH_ITEMS: [],
        MAGIC_ITEMS: [],
    };

    // Return if no cart items
    if (!itemList || itemList.length < 1) {
        return groupItemsList;
    }

    const couponGroupTagIds = new Set<string>();
    if (!isEmpty(groupTagDetails)) {
        groupTagDetails.forEach((tagDetail) => {
            //group_tag_type for both BXGY and SILD is "COUPON"
            //TODO: In Future, Add other group tag types here if needed
            if (tagDetail.group_tag_type === "COUPON") {
                couponGroupTagIds.add(tagDetail.group_tag_id);
            }
        });
    }

    // Iterate over items and push them in respective group list
    itemList.map((item) => {
        if (isFreebieItem(item)) {
            groupItemsList.FREEBIE_ITEMS.push(item);
            return;
        }
        if (isStealDealItem(item)) {
            groupItemsList.STEAL_DEAL_ITEMS.push(item);
            return;
        }

        if (isMagicItem(item)) {
            groupItemsList.MAGIC_ITEMS.push(item);
            return;
        }

        if (isDealRushItem(item)) {
            groupItemsList.DEAL_RUSH_ITEMS.push(item);
            return;
        }
        if (isRegularItem(item)) {
            groupItemsList.REGULAR_ITEMS.push(item);
            return;
        }

        if (isGroupTagtem(item) && item.item_group_tag_id) {
            if (couponGroupTagIds.has(item.item_group_tag_id)) {
                groupItemsList.COUPON_GROUP_ITEMS.push(item);
            } else {
                groupItemsList.SILD_ITEMS.push(item);
            }
            return;
        }
    });

    return groupItemsList;
};

const hasSameAddonsnVariants = (existingItem: CartItemWithUUID, updatingItem: CartItemWithUUID): boolean => {
    const { variants: existingItemVariants = [], addons: existingItemAddons = [] } = existingItem;
    const { variants: updatingItemVariants = [], addons: updatingItemAddons = [] } = updatingItem;

    // if not same length of addons and variants return false
    if (
        existingItemVariants.length !== updatingItemVariants.length ||
        existingItemAddons.length !== updatingItemAddons.length
    ) {
        return false;
    }

    let isSameItem = true;

    const existingItemAddonsList: number | string[] = [];
    const existingItemVariantsList: number | string[] = [];

    existingItemVariants.map((variant) => {
        existingItemVariantsList.push(variant.variation_id.toString());
    });

    existingItemAddons.map((addon) => {
        existingItemAddonsList.push(addon.choice_id.toString());
    });

    updatingItemVariants.map((variant) => {
        if (!existingItemVariantsList.includes(variant.variation_id.toString())) {
            isSameItem = false;
        }
    });

    updatingItemAddons.map((addon) => {
        if (!existingItemAddonsList.includes(addon.choice_id.toString())) {
            isSameItem = false;
        }
    });

    return isSameItem;
};

export const checkUpdatedItemAlreadyExistInItemsList = (
    otherItemsList: CartItemWithUUID[],
    updatingItem: CartItemWithUUID,
): boolean => {
    let result = false;
    otherItemsList &&
        otherItemsList.map((itemData) => {
            if (itemData.menu_item_id === updatingItem.menu_item_id && hasSameAddonsnVariants(itemData, updatingItem)) {
                result = true;
            }
        });
    return result;
};

export const getExistingCartItem = (
    otherItemsList: CartItemWithUUID[],
    updatingItem: CartItemWithUUID,
): CartItemWithUUID | null => {
    let item: CartItemWithUUID | null = null;
    otherItemsList &&
        otherItemsList.map((itemData) => {
            if (itemData.menu_item_id === updatingItem.menu_item_id && hasSameAddonsnVariants(itemData, updatingItem)) {
                item = itemData;
            }
        });

    return item;
};

export const getItemsListForCartUpdate = (itemsList: CartItemWithUUID[]): ApiMenuCartItem[] => {
    const menuItem: ApiMenuCartItem[] = [];
    if (itemsList && itemsList.length > 0) {
        // Check if only steal deal items are present in the list
        const nonStealDealItems = itemsList.filter((item) => !isStealDealItem(item));
        if (nonStealDealItems.length === 0) {
            // If only steal deal items are present, return empty list
            return [];
        }

        // sort items based on position to maintain their position level
        const sortedItemList = itemsList.sort((a, b) => {
            if (a.itemPosition > b.itemPosition) {
                return 1;
            }
            return b.itemPosition > a.itemPosition ? -1 : 0;
        });
        sortedItemList.map((item) => {
            menuItem.push(getCartMenuItemPropertyForUpdate(item));
        });
    }

    return menuItem;
};

export const isCartTotalZero = (cartResponse: ReviewedCart | null) => {
    return cartResponse?.data?.cart_total === 0;
};

export const isOnTimeCart = (promises: IDeliveryPromise[] | undefined): boolean => {
    return (
        promises?.some((promise: IDeliveryPromise) => promise.type === DeliveryPromiseType.ONE_TIME_PROMISE) || false
    );
};

export const isGroupOrderFlow = (
    props: CartItemRendererProps | GroupCartItemRendererProps,
): props is GroupCartItemRendererProps => {
    return ("isGroupOrderFlow" in props && props.isGroupOrderFlow) || false;
};

export const isItemExceededLimitMessageExists = (cartResponse: ReviewedCart | null) => {
    return !isEmpty(cartResponse?.data?.deals_message_info?.limit_exceeded_message);
};

/**
 * Creates a section icon React element from a logo ID
 * @param logoId - Optional logo ID from Cloudinary
 * @returns React element or undefined if no logo ID provided
 */
const createSectionIcon = (logoId?: string): React.ReactNode | undefined => {
    if (!logoId) return undefined;

    return React.createElement(Image, {
        cloudinaryId: logoId,
        alt: "section-logo",
        width: "12px",
        height: "12px",
        cloudinaryHeight: 24,
        cloudinaryWidth: 24,
    });
};

/**
 * Group items by selective discounting card sections
 * @param itemList
 * @param selectiveDiscountingCardList
 */
export const getSelectiveDiscountingSections = (
    itemList: CartItemWithUUID[],
    selectiveDiscountingCardList?: CartMenuItemsHeader[],
): SelectiveDiscountingSection[] => {
    if (!selectiveDiscountingCardList || isEmpty(selectiveDiscountingCardList)) {
        return [];
    }

    // Collect all specific groupTagIds (non-empty) from the header list
    const specificGroupTagIds = new Set(
        selectiveDiscountingCardList.filter((card) => !isEmpty(card.groupTagId)).map((card) => card.groupTagId),
    );

    const sections: SelectiveDiscountingSection[] = [];
    const processedGroupTagIds = new Set<string>();

    selectiveDiscountingCardList.forEach((card) => {
        const cardGroupTagId = card.groupTagId ?? "";
        if (processedGroupTagIds.has(cardGroupTagId)) {
            return;
        }

        const items = itemList.filter((item) => {
            if (isEmpty(card.groupTagId)) {
                // Catch-all: items whose groupTagId doesn't match any specific header
                return !specificGroupTagIds.has(item.item_group_tag_id ?? "");
            }
            // Specific match
            return item.item_group_tag_id === card.groupTagId;
        });

        if (!isEmpty(items)) {
            processedGroupTagIds.add(cardGroupTagId);
            const icon = createSectionIcon(card.logoId);

            sections.push({
                header: {
                    title: card.title,
                    subtitle: card.subtitle,
                    subtitleColor: card.subtitleColor,
                    icon,
                    lottieId: card?.lottieId,
                    backgroundColor: card?.backgroundColor,
                    showAnimation: card?.showAnimation,
                },
                titleColor: card.titleColor || "positive",
                items,
            });
        }
    });

    return sections;
};
